import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, FileText, Stethoscope, Heart, BookOpen, 
  Users, Mic, Plus, Clock, ChevronRight, Paperclip,
  Activity
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample recent sessions
const recentSessions = [
  { id: 1, title: "Headache Analysis", tool: "diagnose", date: "Today" },
  { id: 2, title: "Blood Test Report", tool: "report", date: "Yesterday" },
  { id: 3, title: "Diabetes Research", tool: "research", date: "2 days ago" },
];

const UnifiedDashboard = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (response.data?.name) {
          setUserName(response.data.name);
        }
        const savedRole = localStorage.getItem('medplexity_user_role');
        if (savedRole) {
          const roleMap = {
            'doctor': 'Chief of Medicine',
            'patient': 'Patient',
            'researcher': 'Medical Researcher',
            'clinic': 'Healthcare Provider'
          };
          setUserRole(roleMap[savedRole] || 'User');
        }
      } catch (error) {
        console.log('User not authenticated');
      }
    };
    fetchUserInfo();
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    navigate(`/disease/${searchQuery}`, { state: { query: searchQuery } });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const sidebarTools = [
    { id: 'diagnose', icon: Stethoscope, label: "Let's Diagnose", path: '/diagnosis' },
    { id: 'health', icon: FileText, label: 'My Health Record', path: '/history' },
    { id: 'research', icon: BookOpen, label: "Let's Research", path: '/research' },
    { id: 'doctor', icon: Users, label: 'Find a Doctor', path: '/consult-doctor' },
    { id: 'medscribe', icon: Mic, label: 'MedScribe AI', path: null, comingSoon: true },
  ];

  const exploreModules = [
    { id: 'health', icon: FileText, label: 'Health Record', path: '/history' },
    { id: 'research', icon: BookOpen, label: 'Research', path: '/research' },
    { id: 'doctor', icon: Users, label: 'Find a Doctor', path: '/consult-doctor' },
    { id: 'medscribe', icon: Mic, label: 'MedScribe AI', path: null, comingSoon: true },
  ];

  return (
    <div className="unified-dashboard-new">
      {/* Sidebar */}
      <aside className="sidebar-new">
        {/* Logo */}
        <div className="sidebar-logo">
          <div className="logo-icon">
            <Activity size={20} />
          </div>
          <div className="logo-text">
            <span className="logo-title">MedPlexity</span>
            <span className="logo-subtitle">Clinical Intelligence</span>
          </div>
        </div>

        {/* New Session Button */}
        <button 
          className="new-session-btn"
          onClick={() => setSearchQuery('')}
        >
          <Plus size={18} />
          <span>New Session</span>
        </button>

        {/* Recent Sessions */}
        <div className="recent-sessions">
          <div className="recent-header">
            <Clock size={16} />
            <span>Recent Sessions</span>
          </div>
          <div className="sessions-list">
            {recentSessions.map(session => (
              <div key={session.id} className="session-item">
                <span className="session-title">{session.title}</span>
                <span className="session-date">{session.date}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Tools List */}
        <nav className="sidebar-nav">
          {sidebarTools.map(tool => (
            <button
              key={tool.id}
              className={`nav-item ${tool.comingSoon ? 'coming-soon' : ''}`}
              onClick={() => !tool.comingSoon && navigate(tool.path)}
              disabled={tool.comingSoon}
            >
              <tool.icon size={18} />
              <span>{tool.label}</span>
              {tool.comingSoon && <span className="soon-badge">Soon</span>}
            </button>
          ))}
        </nav>

        {/* User Profile */}
        <div className="sidebar-profile" onClick={() => navigate('/profile')}>
          <div className="profile-avatar">
            {userName ? userName[0].toUpperCase() : 'U'}
          </div>
          <div className="profile-info">
            <span className="profile-name">{userName || 'User'}</span>
            <span className="profile-role">{userRole || 'MedPlexity User'}</span>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content-new">
        <div className="content-wrapper">
          {/* Hero Section */}
          <div className="hero-section">
            <h1 className="hero-title">
              What would you like to understand<br />today?
            </h1>
            <p className="hero-subtitle">
              Access clinical-grade diagnostics, interpret complex medical reports, and<br />
              gather research insights instantly.
            </p>
          </div>

          {/* Search Bar */}
          <div className="search-container-new">
            <div className="search-box-new">
              <div className="search-icon-wrapper">
                <Search size={20} />
              </div>
              <input
                type="text"
                placeholder="Ask anything or upload a medical report..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input-new"
              />
              <div className="search-actions">
                <button className="action-btn" title="Attach file">
                  <Paperclip size={20} />
                </button>
                <button className="action-btn" title="Voice input">
                  <Mic size={20} />
                </button>
                <button 
                  className="submit-btn"
                  onClick={handleSearch}
                  disabled={!searchQuery.trim()}
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
            <div className="powered-badge">
              <span className="badge-dot"></span>
              <span>POWERED BY MEDCORE-1 CLINICAL REASONING ENGINE</span>
            </div>
          </div>

          {/* Main Feature Cards */}
          <div className="feature-cards">
            <div 
              className="feature-card diagnose-card"
              onClick={() => navigate('/diagnosis')}
            >
              <div className="card-icon diagnose-icon">
                <Stethoscope size={24} />
              </div>
              <h3 className="card-title">Let's Diagnose</h3>
              <p className="card-description">
                Analyze symptoms, medical history, and risk factors to generate potential differential diagnostics.
              </p>
            </div>

            <div 
              className="feature-card report-card"
              onClick={() => navigate('/labs')}
            >
              <div className="card-icon report-icon">
                <FileText size={24} />
              </div>
              <h3 className="card-title">Interpret a Report</h3>
              <p className="card-description">
                Upload lab results, imaging reports, or EHR exports for a detailed clinical interpretation.
              </p>
            </div>
          </div>

          {/* Explore Modules */}
          <div className="explore-section">
            <h4 className="explore-title">EXPLORE MODULES</h4>
            <div className="module-grid">
              {exploreModules.map(module => (
                <button
                  key={module.id}
                  className={`module-card ${module.comingSoon ? 'coming-soon' : ''}`}
                  onClick={() => !module.comingSoon && navigate(module.path)}
                  disabled={module.comingSoon}
                >
                  <module.icon size={24} />
                  <span>{module.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Collaborate Banner */}
          <div className="collab-banner">
            <div className="banner-content">
              <h4 className="banner-title">Collaborate with Experts</h4>
              <p className="banner-text">
                MedPlexity now supports real-time multi-physician consultation sessions.
              </p>
            </div>
            <button className="banner-btn">Learn More</button>
          </div>
        </div>
      </main>

      <style jsx>{`
        .unified-dashboard-new {
          display: flex;
          min-height: 100vh;
          background: #FAFAFA;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        /* Sidebar Styles */
        .sidebar-new {
          width: 260px;
          background: #FFFFFF;
          border-right: 1px solid #E5E7EB;
          display: flex;
          flex-direction: column;
          padding: 20px 16px;
          position: fixed;
          left: 0;
          top: 0;
          bottom: 0;
          z-index: 100;
        }

        .sidebar-logo {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          padding: 0 4px;
        }

        .logo-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .logo-text {
          display: flex;
          flex-direction: column;
        }

        .logo-title {
          font-size: 18px;
          font-weight: 700;
          color: #1F2937;
        }

        .logo-subtitle {
          font-size: 11px;
          color: #6B7280;
          letter-spacing: 0.3px;
        }

        .new-session-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          padding: 12px 16px;
          background: linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%);
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 24px;
        }

        .new-session-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
        }

        .recent-sessions {
          margin-bottom: 24px;
        }

        .recent-header {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #F97316;
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 12px;
          padding: 0 4px;
        }

        .sessions-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .session-item {
          display: flex;
          flex-direction: column;
          padding: 10px 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .session-item:hover {
          background: #F3F4F6;
        }

        .session-title {
          font-size: 13px;
          color: #374151;
          font-weight: 500;
        }

        .session-date {
          font-size: 11px;
          color: #9CA3AF;
          margin-top: 2px;
        }

        .sidebar-nav {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          background: none;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          color: #4B5563;
          cursor: pointer;
          transition: all 0.2s;
          text-align: left;
          width: 100%;
        }

        .nav-item:hover:not(.coming-soon) {
          background: #F3F4F6;
          color: #1F2937;
        }

        .nav-item.coming-soon {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .soon-badge {
          margin-left: auto;
          font-size: 10px;
          padding: 2px 6px;
          background: #E5E7EB;
          border-radius: 4px;
          color: #6B7280;
        }

        .sidebar-profile {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          border-top: 1px solid #E5E7EB;
          margin-top: auto;
          cursor: pointer;
          border-radius: 8px;
          transition: background 0.2s;
        }

        .sidebar-profile:hover {
          background: #F3F4F6;
        }

        .profile-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 14px;
        }

        .profile-info {
          display: flex;
          flex-direction: column;
        }

        .profile-name {
          font-size: 13px;
          font-weight: 600;
          color: #1F2937;
        }

        .profile-role {
          font-size: 11px;
          color: #6B7280;
        }

        /* Main Content Styles */
        .main-content-new {
          flex: 1;
          margin-left: 260px;
          padding: 40px 60px;
          display: flex;
          justify-content: center;
        }

        .content-wrapper {
          max-width: 900px;
          width: 100%;
        }

        .hero-section {
          text-align: center;
          margin-bottom: 32px;
        }

        .hero-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 42px;
          font-weight: 600;
          color: #1F2937;
          line-height: 1.2;
          margin: 0 0 16px 0;
        }

        .hero-subtitle {
          font-size: 15px;
          color: #6B7280;
          line-height: 1.6;
          margin: 0;
        }

        /* Search Box */
        .search-container-new {
          margin-bottom: 40px;
        }

        .search-box-new {
          display: flex;
          align-items: center;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 16px;
          padding: 8px 8px 8px 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: all 0.2s;
        }

        .search-box-new:focus-within {
          border-color: #7C3AED;
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
        }

        .search-icon-wrapper {
          color: #9CA3AF;
          margin-right: 12px;
        }

        .search-input-new {
          flex: 1;
          border: none;
          outline: none;
          font-size: 15px;
          color: #1F2937;
          background: transparent;
          padding: 12px 0;
        }

        .search-input-new::placeholder {
          color: #9CA3AF;
        }

        .search-actions {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .action-btn {
          width: 40px;
          height: 40px;
          border: none;
          background: none;
          color: #6B7280;
          cursor: pointer;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .action-btn:hover {
          background: #F3F4F6;
          color: #1F2937;
        }

        .submit-btn {
          width: 44px;
          height: 44px;
          border: none;
          background: linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%);
          color: white;
          cursor: pointer;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .submit-btn:hover:not(:disabled) {
          transform: scale(1.05);
        }

        .submit-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .powered-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 12px;
          font-size: 11px;
          color: #6B7280;
          letter-spacing: 0.5px;
        }

        .badge-dot {
          width: 6px;
          height: 6px;
          background: #10B981;
          border-radius: 50%;
        }

        /* Feature Cards */
        .feature-cards {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-bottom: 40px;
        }

        .feature-card {
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 16px;
          padding: 28px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .feature-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        .card-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .diagnose-icon {
          background: rgba(124, 58, 237, 0.1);
          color: #7C3AED;
        }

        .report-icon {
          background: rgba(59, 130, 246, 0.1);
          color: #3B82F6;
        }

        .card-title {
          font-size: 18px;
          font-weight: 600;
          color: #1F2937;
          margin: 0 0 8px 0;
        }

        .card-description {
          font-size: 14px;
          color: #6B7280;
          line-height: 1.5;
          margin: 0;
        }

        /* Explore Section */
        .explore-section {
          margin-bottom: 40px;
        }

        .explore-title {
          font-size: 12px;
          font-weight: 600;
          color: #9CA3AF;
          letter-spacing: 1px;
          margin: 0 0 16px 0;
        }

        .module-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .module-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 24px 16px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s;
          color: #4B5563;
          font-size: 13px;
          font-weight: 500;
        }

        .module-card:hover:not(.coming-soon) {
          border-color: #7C3AED;
          color: #7C3AED;
        }

        .module-card.coming-soon {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Collab Banner */
        .collab-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: linear-gradient(135deg, #7C3AED 0%, #3B82F6 100%);
          border-radius: 16px;
          padding: 24px 32px;
          color: white;
        }

        .banner-title {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
        }

        .banner-text {
          font-size: 14px;
          opacity: 0.9;
          margin: 0;
        }

        .banner-btn {
          background: white;
          color: #7C3AED;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .banner-btn:hover {
          transform: scale(1.05);
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .main-content-new {
            padding: 30px 40px;
          }
          
          .feature-cards {
            grid-template-columns: 1fr;
          }
          
          .module-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .sidebar-new {
            transform: translateX(-100%);
            position: fixed;
          }
          
          .main-content-new {
            margin-left: 0;
            padding: 20px;
          }
          
          .hero-title {
            font-size: 28px;
          }
          
          .collab-banner {
            flex-direction: column;
            text-align: center;
            gap: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default UnifiedDashboard;
